import React, { ReactElement, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { t } from '@sm/intl';
import { Box } from '@wds/box';

import FrequentlyAskedQuestion from './components/FAQ';
import FAQAnswer from './components/FAQAnswer';
import usePricingFooterStyles from './useStyles';

import COPY from './copy';
import { MoreAboutProps } from './types';
import { Packages, ROOT_PRICING_ROUTE } from '~shared/constants/pricing';
import ProgramIDs from '~shared/constants/sales';
import { usePricingState } from '~shared/hooks/usePricingState';
import useExperimentAssignment from '../../hooks/useExperimentAssignment';
import { switchAndSave } from '../../experiments/grow_2792_pnp_refresh_v1/helpers';
import { logPricingPageLoad } from '../../pages/shared';

export default function MoreAbout({
  displayTeamsDiscountFAQ,
  teamsDiscountPercent,
  pageUtSource,
  pageName,
  utSource2,
  utSource3,
  displayEduFaq, // optional since most experiments currently exclude edu_faq
}: Readonly<MoreAboutProps>): ReactElement {
  const { PricingList } = usePricingFooterStyles();

  const {
    state: {
      packages: { displayPackageSet },
    },
    dispatch,
  } = usePricingState();
  const { isPnPRefresh } = useExperimentAssignment();
  const isAdvantageMonthlyCurrentlySelected = displayPackageSet.includes(Packages.AdvantageMonthly);

  const handleSwitchAndSave = useCallback(() => {
    switchAndSave({
      numberOfUsers: '3',
      isAdvantagePackage: true,
      isPremierPackage: false,
      pageName,
      dispatch,
      isAdvantageMonthlyCurrentlySelected,
    }); // update Advantage

    switchAndSave({ numberOfUsers: '3', isAdvantagePackage: false, isPremierPackage: true, pageName, dispatch }); // update Premier

    logPricingPageLoad({ pageName, utSource: 'pricing-summary-more-about-teams' });
  }, [dispatch, isAdvantageMonthlyCurrentlySelected, pageName]);

  return (
    <Box className={PricingList} data-testid="MoreAbout__Section">
      {displayEduFaq && (
        <FrequentlyAskedQuestion
          data-testid="MoreAbout__EduPricing"
          heading={t(COPY.SPECIAL_PRICING_FOR_EDUCATORS_FAQ)}
        >
          <FAQAnswer>
            {t(COPY.SPECIAL_PRICING_FOR_EDUCATORS_ANSWER)}{' '}
            <Link to={`${ROOT_PRICING_ROUTE}/educational/?ut_source=${pageUtSource}`}>{t(COPY.SEE_PRICING)}</Link>
          </FAQAnswer>
        </FrequentlyAskedQuestion>
      )}

      <FrequentlyAskedQuestion heading={t(COPY.CORPORATE_PLANS_FAQ)}>
        <FAQAnswer>
          {t(COPY.CORPORATE_PLANS_ANSWER)}{' '}
          <a
            href={`/mp/ipas-contact-sales/?program=${ProgramIDs.ContactSalesPNP}&ut_source=pricing_page&ut_source2=${utSource2}&ut_source3=${utSource3}`}
            target="_blank"
            rel="noreferrer"
          >
            {t(COPY.CONTACT_SALES)}
          </a>
        </FAQAnswer>
      </FrequentlyAskedQuestion>

      {displayTeamsDiscountFAQ && (
        <FrequentlyAskedQuestion heading={t(COPY.DISCOUNTS_FOR_TEAMS_FAQ)}>
          <FAQAnswer>
            {t(COPY.DISCOUNTS_FOR_TEAMS_ANSWER, { teamsMinimumDiscount: teamsDiscountPercent })}{' '}
            {isPnPRefresh ? (
              <a href="#packages" onClick={handleSwitchAndSave}>
                {t(COPY.SEE_PRICING)}
              </a>
            ) : (
              <Link to={`${ROOT_PRICING_ROUTE}/teams/details/?ut_source=${pageUtSource}`}>{t(COPY.SEE_PRICING)}</Link>
            )}
          </FAQAnswer>
        </FrequentlyAskedQuestion>
      )}
    </Box>
  );
}
